import { FC, useEffect, useRef, useState } from 'react'
import { useLayout } from '../../../_metronic/layout/core'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { KTSVG } from '../../../_metronic/helpers'
import { LoaderOverlay } from '../../hooks/loader/LoaderOverlay'
import { showErrorAlert } from '../../helpers/alert'
import { ReferMember } from './models/referralModels'
import { createMemeberReferral } from '../../services/referralsServices'
import { AutocompleteReferCatalogField } from './fields/AutocompleteReferCatalogField'
import PhoneInput from 'react-phone-input-2'
import { useConfirmAction } from '../../hooks/utils/useConfirmAction'

const ReferMemberSchema = Yup.object().shape({
  first_name: Yup.string().required('Name is required'),
  last_name: Yup.string().required('Surname is required'),
  phone_number: Yup.string()
    .matches(/^[0-9+\s()-]+$/, 'Invalid phone number')
    .required('Phone number is required'),
  email: Yup.string()
    .email('Invalid email')
    .required('Email is required'),
  invitee_qualification: Yup.number()
    .required('Qualification is required')
    .nullable()
})

const ReferMemberPage: FC = () => {
  const { config, setLayout } = useLayout()
  const restoreRef = useRef(config)
  const [isLoader, setIsLoader] = useState(false)
  const formikRef = useRef<any>(null)
  const confirm = useConfirmAction()

  useEffect(() => {
    restoreRef.current = config
    setLayout({
      ...config,
      header: { ...config.header, display: true },
      aside: { ...config.aside, display: true },
      toolbar: { ...config.toolbar, display: false },
      footer: { ...config.footer, display: true },
    })
    return () => {
      setLayout(restoreRef.current)
    }
  }, [])

  const initialValues: ReferMember = {
    first_name: '',
    last_name: '',
    phone_number: '',
    email: '',
    invitee_qualification: '',
  }

  const onSubmit = async (values: ReferMember) => {
    setIsLoader(true)
    try {
      let isConfirm = await confirm({ title: 'Confirm Referral', message: 'Are you sure you want to submit this referral?' })
      if(isConfirm){
        const response = await createMemeberReferral(values)
        if(response && response?.email){
          showErrorAlert({
            title: 'Thank You',
            message: 'We have received your referral.<br/>Our team will review it with care.',
          })
          formikRef.current.resetForm();
        }
      }
    } finally {
      setIsLoader(false)
    }
  }

  return (
    <>
      <div className="refer-member-page">
        {/* Header */}
        <div className="refer-member-header">
          <h1 className="refer-member-title">Refer a Member</h1>
          <p className="refer-member-description">
            When you refer someone to Nobilis, their application will be carefully reviewed. Upon approval and completion of the initiation process, you will receive platform credits in accordance with the Nobilis Loyalty Program.<br/>
            Thank you for building this community together.
          </p>
        </div>

        {/* Form */}
        <Formik innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={ReferMemberSchema}
          onSubmit={onSubmit}
        >
          {(formik) => {

            const handleQualificationChange = (value: number | null) => {
              formik.setFieldValue('invitee_qualification', value)
            }

            return (
              <Form className="refer-member-form">
                <div className="refer-member-grid mb-7">
                  <div className="form-field">
                    <label>Name</label>
                    <Field name="first_name" className="underline-input" />
                    <ErrorMessage name="first_name" component="div" className="error-text" />
                  </div>
  
                  <div className="form-field">
                    <label>Surname</label>
                    <Field name="last_name" className="underline-input" />
                    <ErrorMessage name="last_name" component="div" className="error-text" />
                  </div>
  
                  <div className="form-field">
                    <label>Phone Numbers</label>
                    <div className='profile-input-wrapper'>
                      <Field name='phone_number'>
                        {({ field, form }: any) => (
                          <PhoneInput
                            country='us'
                            placeholder='+1'
                            containerClass='profile-input'
                            value={field.value || ''}
                            onChange={(val: string) => form.setFieldValue(field.name, val)}
                            onBlur={() => form.setFieldTouched(field.name, true)}
                          />
                        )}
                      </Field>
                    </div>
                    <ErrorMessage name="phone_number" component="div" className="error-text" />
                  </div>
  
                  <div className="form-field">
                    <label>Email</label>
                    <Field name="email" type="email" className="underline-input" />
                    <ErrorMessage name="email" component="div" className="error-text" />
                  </div>
  
                  <div className="form-field full-width">
                    <label>Select invitee qualifications to Nobilis</label>
                    <AutocompleteReferCatalogField value={formik.values.invitee_qualification} onChange={handleQualificationChange}/>
                    <ErrorMessage name="invitee_qualification" component="div" className="error-text" />
                  </div>
                </div>
  
                {/* Submit */}
                <div className='w-100 d-flex flex-row justify-content-end'>
                  <button type="submit" className="tap-add-experience-2-btn-main">
                    <div className="tap-host-button-text tap-flex-center">
                      REFER A MEMBER &nbsp;
                      <KTSVG path="/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg" />
                    </div>
                  </button>
                </div>
              </Form>
            )
          } 
          }
        </Formik>
      </div>

      <LoaderOverlay visible={isLoader} message="loading..." />
    </>
  )
}

export { ReferMemberPage }
